<template>
    <section id="app" class="py-8 bg-white md:py-16">
      <div class="max-w-screen-xl px-4 mx-auto md:px-6 lg:px-8">
        <div class="max-w-4xl mx-auto">
          <!-- Main Heading -->
          <h2 class="mb-5 text-3xl font-bold text-center md:mb-10 md:text-4xl font-ww-medium" v-html="mainHeading"></h2>
  
          <div class="flex flex-col items-center justify-between gap-8 md:flex-row">
            <!-- App Image -->
            <div class="md:w-1/2">
              <nuxt-img
                class="mx-auto shadow-lg rounded-xl"
                 sizes="xs:180px sm:180px md:180px lg:180px xl:180px"
                width="180"
                height="351"
                loading="lazy"
                format="webp"
                :src="imageSrc"
                :alt="imageAlt"
              />
            </div>
  
            <!-- Text Content -->
            <div class="md:w-1/2">
              <h3 class="mb-4 text-xl font-bold text-center md:text-2xl md:text-left font-ww-medium" v-html="featureTitle"></h3>
  
              <!-- Features List -->
              <ul class="mb-8 space-y-3">
                <li v-for="(feature, index) in features" :key="index" class="flex items-start text-sm md:text-base">
                  <svg
                    class="flex-shrink-0 w-5 h-5 mr-2 md:w-6 md:h-6 text-lifeboost-green-200"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span v-html="feature.feature"></span>
                </li>
              </ul>
  
              <!-- Store Buttons -->
              <div class="flex flex-wrap justify-center gap-4 md:justify-start">
                <a :href="appStoreLink" target="_blank" aria-label="App Store" class="hover:scale-105 hover:duration-300">
                  <nuxt-img
                    :src="appStoreImage"
                    alt="App Store"
                    width="135"
                    height="40"
                    loading="lazy"
                    format="webp"
                  />
                </a>
                <a
                  :href="googlePlayLink"
                  target="_blank"
                  aria-label="Google Play"
                  class="hover:scale-105 hover:duration-300"
                >
                  <nuxt-img
                    :src="googlePlayImage"
                    alt="Google Play"
                    width="135"
                    height="40"
                    loading="lazy"
                    format="webp"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </template>
  
  <script setup>
  const props = defineProps({
    mainHeading: {
      type: String,
      default: "Don't Break The Chain",
    },
    imageSrc: {
      type: String,
      default: '/assets/images/460x0w.webp',
    },
    imageAlt: {
      type: String,
      default: 'App Screenshot',
    },
    featureTitle: {
      type: String,
      default: 'Build Better Life Habits',
    },
    features: {
      type: Array,
      default: () => [
        { feature: 'Track any activity to create a lifelong habit!' },
        { feature: 'Visualize your progress with chain calendars' },
        { feature: 'Set reminders so you never forget what you\'re supposed to do! ' },
        { feature: 'Create habits to build a better you for life' },
      ],
    },
    appStoreLink: {
      type: String,
      default: 'https://apps.apple.com/us/app/habits-dont-break-the-chain/id6741651253',
    },
    googlePlayLink: {
      type: String,
      default: 'https://play.google.com/store/apps/details?id=com.app.habitbuilder',
    },
    appStoreImage: {
      type: String,
      default: '/assets/images/app-store.svg',
    },
    googlePlayImage: {
      type: String,
      default: '/assets/images/google-play.svg',
    },
  });
  
  defineBuilderComponent({
    friendlyName: 'FeatureHighlightButtons',
    inputs: [
      {
        name: 'mainHeading',
        type: 'richText',
        friendlyName: 'Main Heading',
        defaultValue: "Don't Break The Chain",
      },
      {
        name: 'featureTitle',
        type: 'text',
        friendlyName: 'Feature Block Title',
        defaultValue: 'Build Better Life Habits',
      },
      {
        name: 'features',
        type: 'list',
        friendlyName: 'Feature List',
        defaultValue: [
          { feature: 'Track any activity to create a lifelong habit!' },
          { feature: 'Visualize your progress with chain calendars' },
          { feature: 'Set reminders so you never forget what you\'re supposed to do! ' },
          { feature: 'Create habits to build a better you for life' },
        ],
        subFields: [
          {
            name: 'feature',
            type: 'text',
            friendlyName: 'Feature Text',
          },
        ],
      },
      {
        name: 'imageSrc',
        type: 'file',
        friendlyName: 'Main Image',
        allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
        defaultValue: '/assets/images/460x0w.webp',
      },
      {
        name: 'imageAlt',
        type: 'text',
        friendlyName: 'Main Image Alt Text',
        defaultValue: 'App Screenshot',
      },
      {
        name: 'appStoreLink',
        type: 'text',
        friendlyName: 'App Store Link',
        defaultValue: 'https://apps.apple.com/us/app/habits-dont-break-the-chain/id6741651253',
      },
      {
        name: 'googlePlayLink',
        type: 'text',
        friendlyName: 'Google Play Link',
        defaultValue: 'https://play.google.com/store/apps/details?id=com.app.habitbuilder',
      },
      {
        name: 'appStoreImage',
        type: 'file',
        friendlyName: 'App Store Image',
        allowedFileTypes: ['svg', 'png', 'webp'],
        defaultValue: '/assets/images/app-store.svg',
      },
      {
        name: 'googlePlayImage',
        type: 'file',
        friendlyName: 'Google Play Image',
        allowedFileTypes: ['svg', 'png', 'webp'],
        defaultValue: '/assets/images/google-play.svg',
      },
    ],
  });
  </script>
  